import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import ordersService, {
  type OrderDetails,
  type HazmatInfoItem,
} from '../../../services/orders.service';
import { Row, Label, Section, Icon, TextInput, SelectInput } from './orderform';
import ChangeTemplateTrigger from './ChangeTemplateTrigger';
import DateTimePicker from '../../../components/DateTimePicker';
import TypeAhead from '../../../components/TypeAhead';
import LocationTypeAhead, { locationDisplayName } from '../../../components/LocationTypeAhead';
import WarningConfirmModal from '../../../components/ui/WarningConfirmModal';
import HazmatAddPopover from './HazmatAddPopover';
import api from '../../../services/api';
import { parseMdYDate, formatMdYDate, parseMdYHm, formatMdYHm } from '../../../utils/dateUtils';
import { masterproSearchPath, proDetailPath } from '../../../utils/proSearch';
import type { LocationInfo } from '../../../types/locations';
import LocationInfoModal from '../../../components/leg/LocationInfoModal';
import CustomerInfoModal from './CustomerInfoModal';
import EntityEditLink from '../../../components/EntityEditLink';
import BrokerInfoModal from './BrokerInfoModal';
import {
  isTemplatePronumber,
  isTemplateApproved,
  templateFieldEditable,
} from '../templateFieldPolicy';
import {
  getCustomers,
  getBrokers,
  getRateZones,
  getCmsCodes,
  getDcMap,
  getCustomerInfo,
  getBrokerInfo,
  type LookupOption,
  type CustomerInfo,
  type BrokerInfo,
} from '../../../services/orderLookups.service';
import { listContacts, type Contact } from '../../../services/contacts.service';
import { useCanEditOrderLcFields } from '../useCanEditOrderLcFields';
import { useCanEditStreetTurnReview, STREET_TURN_REVIEW_COMPONENT_ID } from '../useCanEditStreetTurnReview';
import { useCanEditInvoicingFields, ORDER_INVOICING_FIELD_KEYS } from '../useCanEditInvoicingFields';
import InvoicingFieldGroup from './InvoicingFieldGroup';
import { useOrderFieldDisplayContext } from '../OrderFieldDisplayContext';
import { useCanApproveHazmat, HAZMAT_APPROVAL_COMPONENT_ID } from '../useCanApproveHazmat';
import { useTryPermissions } from '../../../contexts/PermissionContext';
import { useToast } from '../../../contexts/ToastContext';
import PermissionsContainer from '../../../components/PermissionsContainer';
import OrderLcFieldGroup from './OrderLcFieldGroup';
import EdiInfoModal from './EdiInfoModal';
import BulkFieldCheckbox from '../changeAll/BulkFieldCheckbox';

/**
 * Reuse-status dropdown options. V1 stores the status string uppercase;
 * we keep that wire format for save parity and convert to title case
 * for the SelectInput label. Blank covers "no status yet" — the only
 * value that also clears the stamped user + datetime on change.
 */
const REUSE_STATUS_VALUES = [
  '',
  'REQUEST NOT SENT',
  'PENDING',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
] as const;

const toTitleCase = (s: string): string =>
  s
    .toLowerCase()
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

/** V1-parity info icon next to Consignee/Customer/Broker — rendered only when
 *  the recno is set, in both edit and read-only modes. While the info fetch is
 *  in flight the icon is disabled + aria-busy (dimmed) so the click visibly
 *  registered and rapid re-clicks can't stack requests. */
const InfoIconButton: React.FC<{ label: string; onClick: () => void; busy?: boolean }> = ({
  label,
  onClick,
  busy,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    aria-busy={busy || undefined}
    className={`inline-flex items-center justify-center w-5 h-5 rounded hover:bg-slate-100 dark:hover:bg-gray-700 shrink-0${
      busy ? ' opacity-50 cursor-wait' : ''
    }`}
    aria-label={label}
    title={label}
  >
    <Icon.info className="w-3.5 h-3.5 text-slate-600 dark:text-gray-300" />
  </button>
);

/**
 * Display of the V1 "Addl Info" tab. The layout mirrors the V1
 * `@edit_layout_addlinfo` array (cms-master/lib/defs.pl:2591-2599) split
 * into three columns:
 *   1. Customer / Broker / Yard / Drop Release
 *   2. Customs / Inbond / Whse / Temp / Hazmat
 *   3. Audit / Reuse / Trouble Tickets
 *
 * When the `editing` prop is provided, priority V1-editable fields render
 * as inline inputs and save via the same `updateOrderField` mechanism used
 * by OrderFormView. Audit fields (createdby, masterpronum, etc.) stay read-only.
 */

/**
 * Subset of the `useOrderEditing` hook surface that AddlInfoView actually uses.
 * Matches the OrderFormEditing interface used by OrderFormView exactly so the
 * parent can pass the same `editing` prop to both.
 */
export interface AddlInfoEditing {
  editedOrderFields: Record<string, any>;
  updateOrderField: (name: string, value: any) => void;
  /** Re-fetch order details after an immediate sub-record write (hazmat line
   *  add/delete). Present on the full useOrderEditing hook passed by the parent. */
  refresh?: () => void | Promise<void>;
}

export interface AddlInfoViewProps {
  details: Partial<OrderDetails> | null;
  editing?: AddlInfoEditing;
  /** CSR/LC template-edit gate. On a template, fields only open when this is
   *  true (and the field policy allows it). Defaults to editable; ignored for
   *  a normal order. */
  canEditTemplate?: boolean;
  /** Called when the user flips "Deleted" -> Y. ProDetailContainer runs the
   *  eligibility check + reason modal + immediate delete save. When absent the
   *  field falls back to a plain edit (e.g. standalone/testing). */
  onDeleteAttempt?: () => void;
  /** True while the delete-eligibility check runs — locks the Deleted field and
   *  shows a spinner so the flag can't be changed mid-check. */
  deleteChecking?: boolean;
  /** Called after a successful Change Template apply — the container refetches the
   *  order (it rewrote order/legs/invoice/mr-bl, so the view must reload). */
  onTemplateChanged?: () => void;
  /** Create New Order mode: no record exists yet, so every control that writes
   *  straight to the server (Change Template, hazmat line add/delete) is hidden
   *  and Deleted is inert. Ordinary staged fields keep working. */
  createMode?: boolean;
}

const PLACEHOLDER = '—';
const LABEL_W_LEFT = 116;
const LABEL_W_MID = 132;
const LABEL_W_RIGHT = 132;

const s = (v: unknown): string => (v == null ? '' : String(v));

interface ReadValueProps {
  value?: unknown;
  mono?: boolean;
  width?: number;
  ariaLabel?: string;
  /** When set (and the value is non-empty), the value renders as a link that
   *  opens `href` in a new tab, with a trailing open-in-new-tab icon. */
  href?: string;
}
const ReadValue: React.FC<ReadValueProps> = ({ value, mono, width, ariaLabel, href }) => {
  const text = value == null || value === '' ? PLACEHOLDER : String(value);
  const isEmpty = text === PLACEHOLDER;
  const cls = [
    'inline-flex items-center h-6 px-1.5 text-[0.75rem] truncate',
    '',
    isEmpty ? 'text-slate-400 dark:text-gray-500' : 'text-slate-900 dark:text-gray-100',
    width ? '' : 'flex-1 min-w-0',
  ]
    .filter(Boolean)
    .join(' ');
  const style = width ? { width: `${width}px` } : undefined;
  if (href && !isEmpty) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${cls} gap-1 !text-blue-600 dark:!text-blue-400 hover:underline`}
        style={style}
        aria-label={ariaLabel}
        title={text}
      >
        {text}
        <Icon.external className="w-3 h-3 shrink-0" aria-hidden="true" />
      </a>
    );
  }
  return (
    <span className={cls} style={style} aria-label={ariaLabel} title={isEmpty ? undefined : text}>
      {text}
    </span>
  );
};

/**
 * "Search results for this template" URL. Templates are searchable by their
 * "T"-prefixed pronumber, so a bare numeric value is prefixed with "T". Opened
 * in a new tab from the Addl Info → Template field.
 *
 * The `pro` param opens the template's detail directly instead of leaving the
 * user on a result list — which matters for a deleted/closed template, where a
 * bare `q` search returns nothing until "Also search closed" is clicked.
 */
function templateSearchUrl(template: string): string {
  const t = template.trim();
  return proDetailPath(/^\d+$/.test(t) ? `T${t}` : t);
}

const Divider: React.FC = () => (
  <div className="my-1.5 border-t border-dashed border-slate-200 dark:border-gray-700" />
);

const YN: Array<{ value: string; label: string }> = [
  { value: '', label: '—' },
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

// "Deleted" is strictly Y/N — no blank option (an order is either deleted or not).
const YN_ONLY: Array<{ value: string; label: string }> = [
  { value: 'N', label: 'N' },
  { value: 'Y', label: 'Y' },
];

// Hardcoded option lists (legacy defs.pl @*_data), leading '' = blank/unselected.
const DEMURRAGE_REASONS = [
  '', 'CET/MET EXAM', 'CLOSED AREA', 'CUSTOMS HOLD', 'DAMAGED CONTAINER',
  'DC AT CAPACITY', 'DIVERSION', 'FDA HOLD', 'FUMIGATION', 'LATE EMPTY NOTIFY',
  'MISSING DOCS', 'NO DELIVERY ORDER', 'NO DEMURRAGE GUARANTEE', 'NO TRI AXLES',
  'TERMINAL HOLD', 'USDA/AQI/AG HOLD', 'WRONG ETA ON ASN', 'CMC RESPONSIBLE',
  'SSL RESPONSIBLE', 'BROKER RESPONSIBLE', 'OTHER',
];
const INBOND_TYPES = ['', 'T&E', 'IE', 'IT', 'OTHER'];
const INBOND_RESPONSIBLE = ['', 'GWI', 'BILL TO', 'CUSTOMER', 'BROKER', 'OTHER'];
const EXAMINER_OPTIONS = ['', 'OTHER'];
const TEMP_CONTROLLED_OPTIONS = ['', 'Y', 'N'];
const TEMP_SCALE_OPTIONS = ['', 'F', 'C'];
const VENT_SETTING_OPTIONS = ['', 'OPEN', 'CLOSED'];
const GENSET_OPTIONS = ['', 'N', 'Y'];

/**
 * Order-level Move Type values (v1 `movetype`). Matches the authoritative enum
 * in backend contstatusreport.py (MOVETYPES). On a template this is a State-2
 * "key-shape" field (editable while unapproved, locked once approved).
 */
const MOVE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  '',
  'IMPORT',
  'EXPORT',
  'DOMESTIC',
  'INBOUND',
  'OUTBOUND',
  'RAIL TO PORT',
  'REPO',
].map((v) => ({ value: v, label: v || '—' }));

/**
 * "(deleted)" beside a party the order still names but Data Management has retired.
 *
 * Matches the wording Table Maintenance already uses for a soft-deleted referenced
 * entity, so the same fact reads the same way in both places. Deliberately a marker
 * rather than a blocked field: the order genuinely points there and the user needs to
 * see what it points at — 67 open orders carry a retired consignee.
 */
const DeletedPartyMark: React.FC<{ what: string }> = ({ what }) => (
  <span
    className="shrink-0 text-[0.6875rem] font-semibold text-red-600 dark:text-red-400"
    title={`This ${what} has been deleted in Data Management`}
  >
    (deleted)
  </span>
);

const AddlInfoView: React.FC<AddlInfoViewProps> = ({ details, editing, canEditTemplate = true, onDeleteAttempt, deleteChecking = false, onTemplateChanged, createMode = false }) => {
  const d = details ?? {};
  const isExport = d.moveType === 'EXPORT' || d.moveType === 'RAIL TO PORT';
  const hazmatInfo: ReadonlyArray<HazmatInfoItem> = d.hazmatInfo ?? [];
  const hazmatApproval = d.hazmatApproval ?? null;

  const ef = editing?.editedOrderFields ?? {};
  const upd = editing?.updateOrderField;

  // Per-field editability. On a template, gate through the field policy
  // (approval-aware) instead of a bare `editing` check, so State-1 fields
  // render read-only. For an order this reduces to `!!editing` — unchanged.
  const isTemplate = isTemplatePronumber(d.pronumber);
  const isApproved = isTemplate && isTemplateApproved(d);
  const canEdit = (fieldKey: string): boolean =>
    isTemplate
      ? canEditTemplate && !!editing && templateFieldEditable(fieldKey, { isApproved })
      : !!editing;

  // LC-only fields (DC / Dept here; Ports live on Order Info). Same template
  // branch, but the ORDER branch is gated by the order-lc-fields permission
  // (defaultDisabled) instead of bare `!!editing`, so non-LC users can't edit
  // them on a real order — see useCanEditOrderLcFields + OrderLcFieldGroup.
  const canEditOrderLcFields = useCanEditOrderLcFields();
  // Config-driven movetype/invoiced display rules (order_field_display_rules) — the
  // single source of truth for whether a field shows, and whether it is locked, on this
  // movetype and invoiced state. Safe default (no provider) = nothing hidden, nothing
  // readonly (today's behavior). Declared above canEditLc because that gate reads it.
  const disp = useOrderFieldDisplayContext();
  const hidden = (key: string): boolean => disp.isFieldHidden(key);

  // A HANDFUL of these fields are owned by the invoicing desk as much as by the LC desk
  // — V1 grants the invoicingclerk role write on them in cmsacl, see
  // ORDER_INVOICING_FIELD_KEYS — so they answer to a second component. ORed, never
  // ANDed: the invoicing gate can only ADD to a field's audience, and an LC who could
  // edit one yesterday still can whether or not anyone grants the new component. That
  // is also exactly how V1 merges two roles at the same priority.
  const canEditInvoicingFields = useCanEditInvoicingFields();
  const userMayWrite = (fieldKey: string): boolean =>
    canEditOrderLcFields
    || (canEditInvoicingFields && ORDER_INVOICING_FIELD_KEYS.has(fieldKey));

  // `editable = user-may-write AND shown AND not-readonly` — see the same note in
  // OrderFormView. The readonly half is what locks Master Pro on an invoiced order, and
  // what Admin Override (#33501) releases when the rule opted in. It applies to BOTH
  // gates: v1's `orderinvoiced` role is priority 900 and overrides the invoicingclerk
  // role too, which is the rule order_field_display_rules ports.
  const canEditLc = (fieldKey: string): boolean =>
    isTemplate
      ? canEditTemplate && !!editing && templateFieldEditable(fieldKey, { isApproved })
      : !!editing && userMayWrite(fieldKey) && !disp.isFieldReadonly(fieldKey);
  // Street Turn Review is LC-Manager-only (its own component permission), not part
  // of the order-lc-fields grouping — read-only for LCs + Dispatchers.
  const canEditStreetTurnReview = useCanEditStreetTurnReview();

  /** Effective value: edited wins, then details. */
  const fv = (key: string): unknown =>
    ef[key] !== undefined ? ef[key] : (d as any)[key];

  // Cross-field cascade gates (legacy per-field readonly logic):
  //  - temperature trio + genset editable only when temperatureControlled = 'Y'
  //  - examNo / examReleaseDate editable only when examiner is set
  //  - inbondRespOther editable only when inbondResponsible = 'OTHER'
  const tempControlled = s(fv('temperatureControlled')) === 'Y';
  const examinerSet = s(fv('examiner')).trim() !== '';
  const inbondRespOther = s(fv('inbondResponsible')) === 'OTHER';
  // Turning temperature control off (N / blank) clears + locks the trio + genset.
  const handleTempControlledChange = (v: string) => {
    upd?.('temperatureControlled', v);
    if (v !== 'Y') {
      upd?.('temperature', '');
      upd?.('tempScale', '');
      upd?.('ventSetting', '');
      upd?.('genset', '');
    }
  };

  // Inline save-blocking warnings, styled like the Order Info validation banner
  // (these same conditions hard-block the save in validateOrderSave). Shown near
  // the fields they concern so the user sees why the save is blocked in context.
  const renderWarningBox = (warnings: string[]) =>
    warnings.length === 0 ? null : (
      <div
        className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded-md p-2 text-xs text-red-900 dark:text-red-200 space-y-1 my-0.5"
        role="alert"
      >
        <ul className="list-disc pl-4 space-y-0.5">
          {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      </div>
    );
  // Reefer/temperature requirements (mirror validateOrderSave Rule 3).
  const reeferWarnings: string[] = [];
  if (editing) {
    if (/REEFER/i.test(s(fv('equipType'))) && !s(fv('temperatureControlled')).trim()) {
      reeferWarnings.push('Please specify if temperature controlled.');
    }
    if (
      s(fv('temperatureControlled')) === 'Y' &&
      (!s(fv('temperature')).trim() || !s(fv('genset')).trim() ||
        !s(fv('tempScale')).trim() || !s(fv('ventSetting')).trim())
    ) {
      reeferWarnings.push(
        'Genset, temperature, scale, and vent setting are required when temperature-controlled',
      );
    }
  }

  // Consignee / reuse-origin location options are NOT preloaded — both cells
  // run a live server search (LocationTypeAhead), so a location created
  // mid-session is findable immediately.

  // Customer + Broker/Forwarder option lists (typeahead-shaped), fetched once
  // when the form is editable.
  const [customers, setCustomers] = useState<LookupOption[]>([]);
  const [brokers, setBrokers] = useState<LookupOption[]>([]);
  const [rateZones, setRateZones] = useState<LookupOption[]>([]);
  const [cmsCodes, setCmsCodes] = useState<LookupOption[]>([]);
  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    getCustomers().then((r) => { if (!cancelled) setCustomers(r); }).catch(() => { /* empty */ });
    getBrokers().then((r) => { if (!cancelled) setBrokers(r); }).catch(() => { /* empty */ });
    getRateZones().then((r) => { if (!cancelled) setRateZones(r); }).catch(() => { /* empty */ });
    getCmsCodes().then((r) => { if (!cancelled) setCmsCodes(r); }).catch(() => { /* empty */ });
    return () => { cancelled = true; };
  }, [editing]);

  // DC options are scoped to the effective customer (edited wins) and refetch
  // when it changes. An empty result means the customer has no DCs → the DC
  // field falls back to free-text entry.
  const effectiveCustomerCode = String(fv('customer') ?? '');
  const [dcOptions, setDcOptions] = useState<LookupOption[]>([]);
  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    getDcMap(effectiveCustomerCode)
      .then((r) => { if (!cancelled) setDcOptions(r); })
      .catch(() => { if (!cancelled) setDcOptions([]); });
    return () => { cancelled = true; };
  }, [editing, effectiveCustomerCode]);
  const nameByValue = (opts: LookupOption[], value: string): string =>
    opts.find((o) => o.value === value)?.label ?? '';

  // Broker contacts for the currently-selected broker (contactinfo keyed by
  // tablename='brokerforwarder', tablerecno=brokerCode). Refetched whenever the
  // effective broker changes; cleared when no broker is set.
  const effectiveBrokerCode = Number(fv('brokerCode')) || 0;
  const [brokerContacts, setBrokerContacts] = useState<Contact[]>([]);

  // V1-parity info popups for the three entity fields. Fetched per click —
  // the drawer only holds the recnos + display names. While a fetch is in
  // flight `infoLoading` names the pending entity (drives the busy icon and
  // blocks re-entry); a failed or empty fetch surfaces an error toast instead
  // of silently doing nothing.
  const { showToast } = useToast();
  const [consigneeInfo, setConsigneeInfo] = useState<LocationInfo | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [brokerInfo, setBrokerInfo] = useState<BrokerInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState<'consignee' | 'customer' | 'broker' | null>(null);
  const effectiveConsigneeCode = Number(fv('consigneeCode')) || 0;
  const effectiveCustomerRecno = Number(fv('customer')) || 0;
  const openConsigneeInfo = () => {
    if (infoLoading) return;
    setInfoLoading('consignee');
    api.get<Record<string, LocationInfo>>('/admin/locations/details', {
      params: { recnos: String(effectiveConsigneeCode) },
    }).then((res) => {
      const info = res.data?.[String(effectiveConsigneeCode)] ?? null;
      if (info) setConsigneeInfo(info);
      else showToast({ type: 'error', message: 'Failed to load consignee info' });
    })
      .catch(() => showToast({ type: 'error', message: 'Failed to load consignee info' }))
      .finally(() => setInfoLoading(null));
  };
  const openCustomerInfo = () => {
    if (infoLoading) return;
    setInfoLoading('customer');
    getCustomerInfo(effectiveCustomerRecno)
      .then(setCustomerInfo)
      .catch(() => showToast({ type: 'error', message: 'Failed to load customer info' }))
      .finally(() => setInfoLoading(null));
  };
  const openBrokerInfo = () => {
    if (infoLoading) return;
    setInfoLoading('broker');
    getBrokerInfo(effectiveBrokerCode)
      .then(setBrokerInfo)
      .catch(() => showToast({ type: 'error', message: 'Failed to load broker/forwarder info' }))
      .finally(() => setInfoLoading(null));
  };

  // EDI Info modal (V1 "View EDI"), opened from the Created From / Parent row.
  // Shown when the pro HAS EDI RECORDS, and on nothing else. `numEdiFiles` is V1's
  // numedifiles — a live count of edi_orders — so this is exactly the set of pros whose
  // modal has something in it.
  //
  // It used to also fire on createdFrom === 'edi', which surfaced the link before the
  // numEdiFiles field was deployed. That is long past, and the arm outlived its purpose:
  // an EDI-created pro whose records are later removed keeps `createdfrom` forever, so
  // the link stayed and opened an empty modal. 238 pros created in 2026 are in that
  // state. The Invoice List gates on the same thing, so the two screens agree.
  const [ediModalOpen, setEdiModalOpen] = useState(false);
  const hasEdi = (d.numEdiFiles ?? 0) > 0;
  useEffect(() => {
    if (!editing || !effectiveBrokerCode) { setBrokerContacts([]); return; }
    let cancelled = false;
    listContacts('brokerforwarder', effectiveBrokerCode)
      .then((r) => { if (!cancelled) setBrokerContacts(r); })
      .catch(() => { if (!cancelled) setBrokerContacts([]); });
    return () => { cancelled = true; };
  }, [editing, effectiveBrokerCode]);
  const brokerContactOptions = useMemo(
    () => brokerContacts.map((c) => ({ value: String(c.recno), label: c.contactname })),
    [brokerContacts],
  );
  // Phone/email of the currently-selected broker/forwarder contact — reflects
  // an in-session pick (from the fetched contacts) and falls back to the saved
  // values, so the details show under the lookup in both edit + read modes.
  const selectedBrokerContact = brokerContacts.find(
    (c) => String(c.recno) === String(fv('brokerContactRecno') ?? ''),
  );
  const brokerContactPhone = selectedBrokerContact?.contactphone ?? d.brokerContactPhone ?? '';
  const brokerContactEmail = selectedBrokerContact?.contactemail ?? d.brokerContactEmail ?? '';

  // Current user — used to stamp reuseStatusUser when the status changes.
  // Mirrors the pattern used in OrderDetailsModalContent / NotesTab. Falls
  // back to 'system' if MSAL hasn't populated `accounts` (test env, brief
  // post-mount window before auth resolves) so we never write an empty
  // username — V1 treats 'system' as a sentinel for auto-stamps.
  const { accounts } = useMsal();
  const currentUsername = accounts[0]?.username || accounts[0]?.name || 'system';
  const currentUserDisplayName = accounts[0]?.name || currentUsername;

  /**
   * Handle a reuse-status change. V1 stores `now()` for the datetime and
   * the userid for the user. Empty status clears both stamped fields so
   * the audit history shows no active status. Also writes
   * `reuseStatusUserName` for optimistic display — it's not in
   * buildOrderDataRows so it won't persist, but the backend re-joins it
   * after save anyway.
   */
  const handleReuseStatusChange = (next: string) => {
    if (!upd) return;
    upd('reuseStatus', next);
    if (next === '') {
      upd('reuseStatusUser', '');
      upd('reuseStatusUserName', '');
      upd('reuseStatusDatetime', '');
    } else {
      upd('reuseStatusUser', currentUsername);
      upd('reuseStatusUserName', currentUserDisplayName);
      upd('reuseStatusDatetime', formatMdYHm(new Date()));
    }
  };

  // ── Hazmat editing ──────────────────────────────────────────────────────
  // Lines (add/delete) use the standard LC gate; the approval checkbox has its
  // own component permission. V1 getHazmatInfo/savehazmatinfo/deletehazmat.
  // Add/delete hit the hazmat endpoints immediately (no pro to write against
  // pre-create) — in create mode the lines render read-only "from template" and
  // the server re-copies them inside the create transaction.
  const canEditHazLines = canEditLc('hazmatInfo') && !createMode;
  const canApproveHazmat = useCanApproveHazmat();
  const perms = useTryPermissions();
  // Approver stamp — CMS username (falls back to email / msal so it's never blank).
  const hazApprover =
    perms?.permissions?.v1_username || perms?.permissions?.user_email || currentUsername;
  const needHazApproval = !!hazmatApproval?.needApproval;
  const hazDonotHaul = !!hazmatApproval?.donotHaul;
  // Saved approver = ordersall.hazmatapprovedby (surfaced as the top-level field,
  // mirrored on hazmatApproval.approvedBy). Edited value wins for the control.
  const savedHazApprovedBy = String(d.hazmatApprovedBy ?? hazmatApproval?.approvedBy ?? '').trim();
  const effHazApprovedBy = String(ef['hazmatApprovedBy'] ?? savedHazApprovedBy).trim();
  const hazApprovedEffective = effHazApprovedBy !== '';
  const hazApprovalPending =
    ef['hazmatApprovedBy'] !== undefined && effHazApprovedBy !== savedHazApprovedBy;
  const toggleHazApproval = () => {
    if (!upd) return;
    upd('hazmatApprovedBy', hazApprovedEffective ? '' : hazApprover);
  };

  // Hazmat: the lines list + per-row delete stay inline; only the UN-lookup
  // "add" moved into an anchored popover (HazmatAddPopover). hazBusy/hazError
  // are still used by the inline delete below.
  const [hazBusy, setHazBusy] = useState(false);
  const [hazError, setHazError] = useState<string | null>(null);
  const [hazAddOpen, setHazAddOpen] = useState(false);
  const [hazAddButtonRect, setHazAddButtonRect] = useState<DOMRect | null>(null);

  // Hazmat-line delete uses the shared WarningConfirmModal (not a native
  // window.confirm). `hazDeleteConfirm` holds the pending target while the modal
  // is open; confirming runs the delete.
  const [hazDeleteConfirm, setHazDeleteConfirm] = useState<{ recno: number; un: string } | null>(
    null,
  );
  const doDeleteHazLine = async (recno: number) => {
    if (!editing || !d.pronumber) return;
    setHazBusy(true);
    setHazError(null);
    try {
      await ordersService.deleteHazmatLine(d.pronumber, recno);
      await editing.refresh?.();
    } catch (e: any) {
      setHazError(e?.response?.data?.detail || 'Failed to delete hazmat line.');
    } finally {
      setHazBusy(false);
    }
  };

  return (
    <>
      <div className="bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 [&>section+section]:border-l-0 [&>section]:border-slate-100 dark:[&>section]:border-gray-700">
          {/* COLUMN 1 — Customer / Broker / Yard / Drop Release */}
          <Section title="Customer & Yard" icon={<Icon.user className="w-3.5 h-3.5" />}>
            <Row>
              <Label width={LABEL_W_LEFT} bulkFieldName="consigneeCode" bulkEditable={canEditLc('consigneeName')}>Consignee</Label>
              {canEditLc('consigneeName') ? (
                <div className="flex-1 min-w-0">
                  <LocationTypeAhead
                    value={fv('consigneeCode') ? String(fv('consigneeCode')) : ''}
                    valueLabel={String(fv('consigneeName') ?? '')}
                    onPick={(loc) => {
                      if (!upd) return;
                      upd('consigneeCode', loc ? loc.recno : 0);
                      upd('consigneeName', loc ? locationDisplayName(loc) : '');
                    }}
                    compact
                    withRecnoSuffix
                    ariaLabel="Consignee"
                  />
                </div>
              ) : (
                <ReadValue value={d.consigneeName} ariaLabel="Consignee" />
              )}
              {effectiveConsigneeCode > 0 && d.consigneeDeleted && (
                <DeletedPartyMark what="consignee location" />
              )}
              {effectiveConsigneeCode > 0 && (
                <InfoIconButton
                  label="View Consignee Info"
                  onClick={openConsigneeInfo}
                  busy={infoLoading === 'consignee'}
                />
              )}
              <EntityEditLink kind="location" recno={effectiveConsigneeCode} label="Consignee" />
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT} bulkFieldName="customer" bulkEditable={canEditLc('customerName')}>Customer</Label>
              {canEditLc('customerName') ? (
                <div className="flex-1 min-w-0">
                  <TypeAhead
                    options={customers}
                    value={fv('customer') ? String(fv('customer')) : ''}
                    onChange={(next) => {
                      if (!upd) return;
                      upd('customer', next ? Number(next) : 0);
                      upd('customerName', next ? nameByValue(customers, next) : '');
                    }}
                    placeholder="Search customer…"
                    showSearch
                    showDescriptions
                    compact
                    ariaLabel="Customer"
                  />
                </div>
              ) : (
                <ReadValue value={d.customerName} ariaLabel="Customer" />
              )}
              {effectiveCustomerRecno > 0 && d.customerDeleted && (
                <DeletedPartyMark what="customer" />
              )}
              {effectiveCustomerRecno > 0 && (
                <InfoIconButton
                  label="View Customer Info"
                  onClick={openCustomerInfo}
                  busy={infoLoading === 'customer'}
                />
              )}
              <EntityEditLink kind="customer" recno={effectiveCustomerRecno} label="Customer" />
            </Row>
            {!hidden('brokerCode') && (<>
            <Row>
              <Label width={LABEL_W_LEFT} bulkFieldName="brokerCode" bulkEditable={canEditLc('brokerName')}>Broker/Forwarder</Label>
              {canEditLc('brokerName') ? (
                <div className="flex-1 min-w-0">
                  <TypeAhead
                    options={brokers}
                    value={fv('brokerCode') ? String(fv('brokerCode')) : ''}
                    onChange={(next) => {
                      if (!upd) return;
                      upd('brokerCode', next ? Number(next) : 0);
                      upd('brokerName', next ? nameByValue(brokers, next) : '');
                      // Broker changed → the previous contact no longer applies.
                      upd('brokerContactRecno', 0);
                      upd('brokerContactName', '');
                    }}
                    placeholder="Search broker/forwarder…"
                    showSearch
                    showDescriptions
                    compact
                    ariaLabel="Broker/Forwarder"
                  />
                </div>
              ) : (
                <ReadValue value={d.brokerName} ariaLabel="Broker/Forwarder" />
              )}
              {effectiveBrokerCode > 0 && (
                <InfoIconButton
                  label="View Broker/Forwarder Info"
                  onClick={openBrokerInfo}
                  busy={infoLoading === 'broker'}
                />
              )}
              <EntityEditLink kind="broker" recno={effectiveBrokerCode} label="Broker/Forwarder" />
            </Row>
            <Row className="!items-start">
              <Label width={LABEL_W_LEFT} className="mt-1" bulkFieldName="brokerContactRecno" bulkEditable={canEditLc('brokerContactName')}>
                Contact
              </Label>
              {canEditLc('brokerContactName') && effectiveBrokerCode ? (
                <div className="flex-1 min-w-0 flex flex-col gap-[1px]">
                  <SelectInput
                    value={fv('brokerContactRecno') ? String(fv('brokerContactRecno')) : ''}
                    options={[{ value: '', label: PLACEHOLDER }, ...brokerContactOptions]}
                    onChange={(next) => {
                      if (!upd) return;
                      const c = brokerContacts.find((x) => String(x.recno) === next);
                      upd('brokerContactRecno', next ? Number(next) : 0);
                      upd('brokerContactName', c?.contactname ?? '');
                    }}
                    ariaLabel="Broker contact"
                  />
                  {brokerContactPhone && (
                    <span className="text-[0.6875rem] text-slate-600 dark:text-gray-300 truncate" aria-label="Broker contact phone">
                      {brokerContactPhone}
                    </span>
                  )}
                  {brokerContactEmail && (
                    <span className="text-[0.6875rem] text-blue-600 dark:text-blue-400 truncate" aria-label="Broker contact email">
                      {brokerContactEmail}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex-1 min-w-0 flex flex-col gap-[1px]">
                  <span className="text-[0.75rem] text-slate-900 dark:text-gray-100 truncate" aria-label="Broker contact">
                    {s(d.brokerContactName) || PLACEHOLDER}
                  </span>
                  {d.brokerContactPhone && (
                    <span className="text-[0.6875rem] text-slate-600 dark:text-gray-300 truncate" aria-label="Broker contact phone">
                      {d.brokerContactPhone}
                    </span>
                  )}
                  {d.brokerContactEmail && (
                    <span className="text-[0.6875rem] text-blue-600 dark:text-blue-400 truncate" aria-label="Broker contact email">
                      {d.brokerContactEmail}
                    </span>
                  )}
                </div>
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT}>Broker Ref #</Label>
              <ReadValue value={d.brokerRefNo} mono ariaLabel="Broker reference number" />
            </Row>
            </>)}
            <Row>
              <Label width={LABEL_W_LEFT}>Shipper</Label>
              <ReadValue value={d.shipperName} ariaLabel="Shipper" />
            </Row>

            <Divider />

            <OrderLcFieldGroup isTemplate={isTemplate}>
              <Row>
                <Label width={LABEL_W_LEFT}>DC / Dept</Label>
                <BulkFieldCheckbox fieldName="dcNumber" />
                {canEditLc('dcNumber') ? (
                  dcOptions.length > 0 ? (
                    <div className="flex-none w-44">
                      <TypeAhead
                        options={dcOptions}
                        value={s(fv('dcNumber'))}
                        onChange={(next) => upd?.('dcNumber', next ?? '')}
                        placeholder="Select DC…"
                        showSearch
                        compact
                        ariaLabel="DC number"
                      />
                    </div>
                  ) : (
                    // No DCs for this customer → free-text entry.
                    <TextInput
                      value={s(fv('dcNumber'))}
                      onChange={(v) => upd?.('dcNumber', v)}
                      mono
                      width={88}
                      ariaLabel="DC number"
                    />
                  )
                ) : (
                  <ReadValue value={d.dcNumber} mono width={88} ariaLabel="DC number" />
                )}
                <BulkFieldCheckbox fieldName="dept" />
                {canEditLc('dept') ? (
                  <TextInput
                    value={s(fv('dept'))}
                    onChange={(v) => upd?.('dept', v)}
                    mono
                    ariaLabel="Department"
                  />
                ) : (
                  <ReadValue value={d.dept} mono ariaLabel="Department" />
                )}
              </Row>
            </OrderLcFieldGroup>
            <Row>
              <Label width={LABEL_W_LEFT}>Priority / WOS</Label>
              <BulkFieldCheckbox fieldName="priority" />
              {canEditLc('priority') ? (
                <TextInput
                  value={s(fv('priority'))}
                  width={56}
                  onChange={(v) => upd?.('priority', v)}
                  ariaLabel="Priority"
                />
              ) : (
                <ReadValue value={d.priority} width={56} ariaLabel="Priority" />
              )}
              <BulkFieldCheckbox fieldName="priority2" />
              {canEditLc('priority2') ? (
                <TextInput
                  value={s(fv('priority2'))}
                  onChange={(v) => upd?.('priority2', v)}
                  ariaLabel="WOS"
                />
              ) : (
                <ReadValue value={d.priority2} ariaLabel="WOS" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT}>DC Due / ND Reason</Label>
              <BulkFieldCheckbox fieldName="dcDueDate" />
              {canEditLc('dcDueDate') ? (
                <div style={{ width: 88 }}>
                  <DateTimePicker
                    mode="date"
                    inputVariant="compact"
                    selectionType="single"
                    autoApply
                    usePortal
                    openDirection="down"
                    autoFlip
                    labelType="none"
                    placeholder="DC Due"
                    className="w-full [&_div]:!min-w-0"
                    value={parseMdYDate(s(fv('dcDueDate')))}
                    onChange={(v) => upd?.('dcDueDate', formatMdYDate(v as Date | null))}
                  />
                </div>
              ) : (
                <ReadValue value={d.dcDueDate} mono width={88} ariaLabel="DC due date" />
              )}
              <ReadValue value={d.nonDeliveryReason} ariaLabel="Non-delivery reason" />
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT}>DC In / DC Out</Label>
              <BulkFieldCheckbox fieldName="yardReportDatetime" />
              {canEdit('yardReportDatetime') ? (
                <div style={{ width: 120 }}>
                  <DateTimePicker
                    mode="datetime"
                    inputVariant="compact"
                    selectionType="single"
                    use24Hour
                    usePortal
                    openDirection="down"
                    autoFlip
                    labelType="none"
                    placeholder="DC In"
                    className="w-full [&_div]:!min-w-0"
                    value={parseMdYHm(s(fv('yardReportDatetime')))}
                    onChange={(v) => upd?.('yardReportDatetime', formatMdYHm(v as Date | null))}
                  />
                </div>
              ) : (
                <ReadValue value={d.yardReportDatetime} mono width={88} ariaLabel="DC in" />
              )}
              <BulkFieldCheckbox fieldName="notOnYardReportDate" />
              {canEditLc('notOnYardReportDate') ? (
                <div className="flex-1 min-w-0">
                  <DateTimePicker
                    mode="date"
                    inputVariant="compact"
                    selectionType="single"
                    autoApply
                    usePortal
                    openDirection="down"
                    autoFlip
                    labelType="none"
                    placeholder="DC Out"
                    className="w-full [&_div]:!min-w-0"
                    value={parseMdYDate(s(fv('notOnYardReportDate')))}
                    onChange={(v) => upd?.('notOnYardReportDate', formatMdYDate(v as Date | null))}
                  />
                </div>
              ) : (
                <ReadValue value={d.notOnYardReportDate} mono ariaLabel="DC out" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT}>Full/Empty / Yard</Label>
              <BulkFieldCheckbox fieldName="fullEmptyDate" />
              {canEditLc('fullEmptyDate') ? (
                <div style={{ width: 88 }}>
                  <DateTimePicker
                    mode="date"
                    inputVariant="compact"
                    selectionType="single"
                    autoApply
                    usePortal
                    openDirection="down"
                    autoFlip
                    labelType="none"
                    placeholder="Full/Empty"
                    className="w-full [&_div]:!min-w-0"
                    value={parseMdYDate(s(fv('fullEmptyDate')))}
                    onChange={(v) => upd?.('fullEmptyDate', formatMdYDate(v as Date | null))}
                  />
                </div>
              ) : (
                <ReadValue value={d.fullEmptyDate} mono width={88} ariaLabel="Full/Empty date" />
              )}
              <BulkFieldCheckbox fieldName="yardLocation" />
              {canEditLc('yardLocation') ? (
                <TextInput
                  value={s(fv('yardLocation'))}
                  onChange={(v) => upd?.('yardLocation', v)}
                  ariaLabel="Yard location"
                />
              ) : (
                <ReadValue value={d.yardLocation} ariaLabel="Yard location" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT} bulkFieldName="demurrageReason" bulkEditable={canEditLc('demurrageReason')}>Demurrage</Label>
              {canEditLc('demurrageReason') ? (
                <SelectInput
                  value={s(fv('demurrageReason'))}
                  options={DEMURRAGE_REASONS}
                  onChange={(v) => upd?.('demurrageReason', v)}
                  ariaLabel="Demurrage reason"
                />
              ) : (
                <ReadValue value={d.demurrageReason} ariaLabel="Demurrage reason" />
              )}
            </Row>

            {isExport && (
              <>
                <Divider />
                <Row>
                  <Label width={LABEL_W_LEFT}>Drop Rel Date</Label>
                  <ReadValue value={d.dropReleaseDatetime} mono ariaLabel="Drop release date" />
                </Row>
                <Row className="!items-start">
                  <Label width={LABEL_W_LEFT} className="mt-1">Drop Rel Memo</Label>
                  <span className="flex-1 min-w-0 text-[0.75rem] text-slate-900 dark:text-gray-100 whitespace-pre-wrap break-words leading-snug px-1.5">
                    {d.dropReleaseComment || '—'}
                  </span>
                </Row>
              </>
            )}

            <Divider />

            <Row>
              <Label width={LABEL_W_LEFT} bulkFieldName="packaged" bulkEditable={canEditLc('packaged')}>Packaged</Label>
              {canEditLc('packaged') ? (
                <SelectInput
                  value={s(fv('packaged'))}
                  options={YN}
                  width={48}
                  onChange={(v) => upd?.('packaged', v)}
                  ariaLabel="Packaged"
                />
              ) : (
                <ReadValue value={d.packaged} width={32} ariaLabel="Packaged" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT}>Equip Hold / Brokerage</Label>
              <BulkFieldCheckbox fieldName="equipHold" />
              {canEditLc('equipHold') ? (
                <SelectInput
                  value={s(fv('equipHold'))}
                  options={YN}
                  width={48}
                  onChange={(v) => upd?.('equipHold', v)}
                  ariaLabel="Equip Hold"
                />
              ) : (
                <ReadValue value={d.equipHold} width={32} ariaLabel="Equip Hold" />
              )}
              <BulkFieldCheckbox fieldName="allowBrokerage" />
              {canEditLc('allowBrokerage') ? (
                <SelectInput
                  value={s(fv('allowBrokerage'))}
                  options={YN}
                  width={48}
                  onChange={(v) => upd?.('allowBrokerage', v)}
                  ariaLabel="Allow Brokerage"
                />
              ) : (
                <ReadValue value={d.allowBrokerage} width={32} ariaLabel="Allow Brokerage" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_LEFT}>Street Turn Rev/Hold</Label>
              {/* Street Turn Review reuses the `mule` column and is LC-Manager-only
                  (its own street-turn-review component permission, defaultDisabled —
                  read-only for LCs + Dispatchers). Y here blocks a terminate-leg
                  driver (validateChanges). */}
              <PermissionsContainer
                componentId={STREET_TURN_REVIEW_COMPONENT_ID}
                componentName="Street Turn Review"
                defaultDisabled
              >
                {canEditStreetTurnReview && <BulkFieldCheckbox fieldName="streetTurnReview" />}
                {canEditStreetTurnReview ? (
                  <SelectInput
                    value={s(fv('streetTurnReview'))}
                    options={YN}
                    width={48}
                    onChange={(v) => upd?.('streetTurnReview', v)}
                    ariaLabel="Street Turn Review"
                  />
                ) : (
                  <ReadValue value={d.streetTurnReview} width={32} ariaLabel="Street Turn Review" />
                )}
              </PermissionsContainer>
              <BulkFieldCheckbox fieldName="streetTurnHold" />
              {canEdit('streetTurnHold') ? (
                <SelectInput
                  value={s(fv('streetTurnHold'))}
                  options={YN}
                  width={48}
                  onChange={(v) => upd?.('streetTurnHold', v)}
                  ariaLabel="Street Turn Hold"
                />
              ) : (
                <ReadValue value={d.streetTurnHold} width={32} ariaLabel="Street Turn Hold" />
              )}
            </Row>
          </Section>

          {/* COLUMN 2 — Customs / Inbond / Whse / Temp / Hazmat */}
          <Section title="Customs & Warehouse" icon={<Icon.doc className="w-3.5 h-3.5" />}>
            <Row>
              <Label width={LABEL_W_MID}>Customs Exam</Label>
              {canEditLc('isExam') ? (
                <>
                  <BulkFieldCheckbox fieldName="isExam" />
                  <SelectInput
                    value={s(fv('isExam'))}
                    options={YN}
                    width={48}
                    onChange={(v) => upd?.('isExam', v)}
                    ariaLabel="Customs Exam"
                  />
                  <BulkFieldCheckbox fieldName="examiner" />
                  <SelectInput
                    value={s(fv('examiner'))}
                    options={EXAMINER_OPTIONS}
                    onChange={(v) => upd?.('examiner', v)}
                    ariaLabel="Examiner"
                  />
                </>
              ) : (
                <>
                  <ReadValue value={d.isExam} width={32} ariaLabel="Customs Exam" />
                  <ReadValue value={d.examiner} ariaLabel="Examiner" />
                </>
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_MID} bulkFieldName="examNo" bulkEditable={canEditLc('examNo')}>Exam #</Label>
              {canEditLc('examNo') && examinerSet ? (
                <TextInput
                  value={s(fv('examNo'))}
                  mono
                  onChange={(v) => upd?.('examNo', v)}
                  ariaLabel="Exam number"
                />
              ) : (
                <ReadValue value={d.examNo} mono ariaLabel="Exam number" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_MID}>Exam Status / Rls</Label>
              {/* examstatus is always read-only (legacy: a dropdown with no options). */}
              <ReadValue value={d.examStatus} ariaLabel="Exam status" />
              <BulkFieldCheckbox fieldName="examReleaseDate" />
              {canEditLc('examReleaseDate') && examinerSet ? (
                <div className="flex-1 min-w-0">
                  <DateTimePicker
                    mode="date"
                    inputVariant="compact"
                    selectionType="single"
                    autoApply
                    usePortal
                    openDirection="down"
                    autoFlip
                    labelType="none"
                    placeholder="Exam Rls"
                    className="w-full [&_div]:!min-w-0"
                    value={parseMdYDate(s(fv('examReleaseDate')))}
                    onChange={(v) => upd?.('examReleaseDate', formatMdYDate(v as Date | null))}
                  />
                </div>
              ) : (
                <ReadValue value={d.examReleaseDate} mono width={88} ariaLabel="Exam release date" />
              )}
            </Row>

            <Divider />

            <Row>
              <Label width={LABEL_W_MID}>Inbond Type / #</Label>
              {canEditLc('inbondType') ? (
                <>
                  <BulkFieldCheckbox fieldName="inbondType" />
                  <SelectInput
                    value={s(fv('inbondType'))}
                    options={INBOND_TYPES}
                    width={72}
                    onChange={(v) => upd?.('inbondType', v)}
                    ariaLabel="Inbond type"
                  />
                  <BulkFieldCheckbox fieldName="inbondNumber" />
                  <TextInput
                    value={s(fv('inbondNumber'))}
                    onChange={(v) => upd?.('inbondNumber', v)}
                    ariaLabel="Inbond number"
                    placeholder="Number"
                    mono
                  />
                </>
              ) : (
                <>
                  <ReadValue value={d.inbondType} width={56} ariaLabel="Inbond type" />
                  <ReadValue value={d.inbondNumber} mono ariaLabel="Inbond number" />
                </>
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_MID}>Resp Party / Other</Label>
              <BulkFieldCheckbox fieldName="inbondResponsible" />
              {canEditLc('inbondResponsible') ? (
                <SelectInput
                  value={s(fv('inbondResponsible'))}
                  options={INBOND_RESPONSIBLE}
                  onChange={(v) => upd?.('inbondResponsible', v)}
                  ariaLabel="Inbond responsible"
                />
              ) : (
                <ReadValue value={d.inbondResponsible} ariaLabel="Inbond responsible" />
              )}
              <BulkFieldCheckbox fieldName="inbondRespOther" />
              {canEditLc('inbondRespOther') && inbondRespOther ? (
                <TextInput
                  value={s(fv('inbondRespOther'))}
                  onChange={(v) => upd?.('inbondRespOther', v)}
                  ariaLabel="Inbond responsible other"
                />
              ) : (
                <ReadValue value={d.inbondRespOther} ariaLabel="Inbond responsible other" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_MID} bulkFieldName="inbondTenderRcvd" bulkEditable={canEditLc('inbondTenderRcvd')}>Tender Rec'd</Label>
              {canEditLc('inbondTenderRcvd') ? (
                <SelectInput
                  value={s(fv('inbondTenderRcvd'))}
                  options={YN}
                  width={48}
                  onChange={(v) => upd?.('inbondTenderRcvd', v)}
                  ariaLabel="Tender received"
                />
              ) : (
                <ReadValue value={d.inbondTenderRcvd} width={32} ariaLabel="Tender received" />
              )}
            </Row>

            {/* Inbond validation warnings (V1 lib_editrecord.pl:1332-1340). These
                same conditions hard-block the save in validateOrderSave Rule 5;
                shown inline here, red-styled like the Order Info validation banner. */}
            {editing && (() => {
              if (!s(fv('inbondType')).trim()) return null;
              const warnings: string[] = [];
              if (!s(fv('inbondNumber')).trim()) {
                warnings.push('Inbond Number is required when Inbond Type is set');
              }
              if (!s(fv('inbondResponsible')).trim()) {
                warnings.push('Inbond Responsible is required when Inbond Type is set');
              }
              return renderWarningBox(warnings);
            })()}

            {/* Warehouse-entry rows: config-governed (hidden on EXPORT/OUTBOUND per
                the seed; all four rows share that hide-set, so one guard covers the
                group + its Divider). Replaces the old !isExport wrapper. */}
            {!hidden('whseEntryNum') && (
              <>
                <Divider />
                <Row>
                  <Label width={LABEL_W_MID} bulkFieldName="whseEntryNum" bulkEditable={canEditLc('whseEntryNum')}>Whse Entry #</Label>
                  {canEditLc('whseEntryNum') ? (
                    <TextInput
                      value={s(fv('whseEntryNum'))}
                      mono
                      onChange={(v) => upd?.('whseEntryNum', v)}
                      ariaLabel="Warehouse entry number"
                    />
                  ) : (
                    <ReadValue value={d.whseEntryNum} mono ariaLabel="Warehouse entry number" />
                  )}
                </Row>
                <Row>
                  <Label width={LABEL_W_MID}>3461 / Red Stamp</Label>
                  <BulkFieldCheckbox fieldName="whse3461Date" />
                  {canEditLc('whse3461Date') ? (
                    <div style={{ width: 88 }}>
                      <DateTimePicker
                        mode="date" inputVariant="compact" selectionType="single" autoApply
                        usePortal openDirection="down" autoFlip labelType="none" placeholder="3461"
                        className="w-full [&_div]:!min-w-0"
                        value={parseMdYDate(s(fv('whse3461Date')))}
                        onChange={(v) => upd?.('whse3461Date', formatMdYDate(v as Date | null))}
                      />
                    </div>
                  ) : (
                    <ReadValue value={d.whse3461Date} mono width={88} ariaLabel="3461 date" />
                  )}
                  <BulkFieldCheckbox fieldName="whseRedStampDate" />
                  {canEditLc('whseRedStampDate') ? (
                    <div className="flex-1 min-w-0">
                      <DateTimePicker
                        mode="date" inputVariant="compact" selectionType="single" autoApply
                        usePortal openDirection="down" autoFlip labelType="none" placeholder="Red Stamp"
                        className="w-full [&_div]:!min-w-0"
                        value={parseMdYDate(s(fv('whseRedStampDate')))}
                        onChange={(v) => upd?.('whseRedStampDate', formatMdYDate(v as Date | null))}
                      />
                    </div>
                  ) : (
                    <ReadValue value={d.whseRedStampDate} mono ariaLabel="Red stamp date" />
                  )}
                </Row>
                <Row>
                  <Label width={LABEL_W_MID} bulkFieldName="zeroRecordDate" bulkEditable={canEditLc('zeroRecordDate')}>0 Record Date</Label>
                  {canEditLc('zeroRecordDate') ? (
                    <div style={{ width: 88 }}>
                      <DateTimePicker
                        mode="date" inputVariant="compact" selectionType="single" autoApply
                        usePortal openDirection="down" autoFlip labelType="none" placeholder="0 Record"
                        className="w-full [&_div]:!min-w-0"
                        value={parseMdYDate(s(fv('zeroRecordDate')))}
                        onChange={(v) => upd?.('zeroRecordDate', formatMdYDate(v as Date | null))}
                      />
                    </div>
                  ) : (
                    <ReadValue value={d.zeroRecordDate} mono ariaLabel="Zero record date" />
                  )}
                </Row>
                <Row>
                  <Label width={LABEL_W_MID}>Rendered Deadline / Date</Label>
                  {canEditLc('renderedDeadlineDate') ? (
                    <>
                      <BulkFieldCheckbox fieldName="renderedDeadlineDate" />
                      <div style={{ width: 108 }}>
                        <DateTimePicker
                          mode="date"
                          inputVariant="compact"
                          selectionType="single"
                          autoApply
                          usePortal
                          openDirection="down"
                          autoFlip
                          labelType="none"
                          placeholder="Deadline"
                          className="w-full [&_div]:!min-w-0"
                          value={parseMdYDate(s(fv('renderedDeadlineDate')))}
                          onChange={(v) => upd?.('renderedDeadlineDate', formatMdYDate(v as Date | null))}
                        />
                      </div>
                      <BulkFieldCheckbox fieldName="renderedDate" />
                      <div className="flex-1 min-w-0">
                        <DateTimePicker
                          mode="date"
                          inputVariant="compact"
                          selectionType="single"
                          autoApply
                          usePortal
                          openDirection="down"
                          autoFlip
                          labelType="none"
                          placeholder="Rendered"
                          className="w-full [&_div]:!min-w-0"
                          value={parseMdYDate(s(fv('renderedDate')))}
                          onChange={(v) => upd?.('renderedDate', formatMdYDate(v as Date | null))}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <ReadValue value={d.renderedDeadlineDate} mono width={88} ariaLabel="Rendered deadline" />
                      <ReadValue value={d.renderedDate} mono ariaLabel="Rendered date" />
                    </>
                  )}
                </Row>
              </>
            )}

            <Divider />

            <Row>
              <Label width={LABEL_W_MID}>Temp Ctrl / Genset</Label>
              <BulkFieldCheckbox fieldName="temperatureControlled" />
              {canEditLc('temperatureControlled') ? (
                <SelectInput
                  value={s(fv('temperatureControlled'))}
                  options={TEMP_CONTROLLED_OPTIONS}
                  width={48}
                  onChange={handleTempControlledChange}
                  ariaLabel="Temperature controlled"
                />
              ) : (
                <ReadValue value={d.temperatureControlled} width={32} ariaLabel="Temperature controlled" />
              )}
              <BulkFieldCheckbox fieldName="genset" />
              {canEditLc('genset') && tempControlled ? (
                <SelectInput
                  value={s(fv('genset'))}
                  options={GENSET_OPTIONS}
                  width={48}
                  onChange={(v) => upd?.('genset', v)}
                  ariaLabel="Genset required"
                />
              ) : (
                <ReadValue value={d.genset} width={32} ariaLabel="Genset required" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_MID}>Temp / Vent</Label>
              <BulkFieldCheckbox fieldName="temperature" />
              {canEditLc('temperature') && tempControlled ? (
                <TextInput
                  value={s(fv('temperature'))}
                  mono
                  width={56}
                  onChange={(v) => upd?.('temperature', v)}
                  ariaLabel="Temperature"
                />
              ) : (
                <ReadValue value={d.temperature} mono width={48} ariaLabel="Temperature" />
              )}
              <BulkFieldCheckbox fieldName="tempScale" />
              {canEditLc('tempScale') && tempControlled ? (
                <SelectInput
                  value={s(fv('tempScale'))}
                  options={TEMP_SCALE_OPTIONS}
                  width={48}
                  onChange={(v) => upd?.('tempScale', v)}
                  ariaLabel="Temperature scale"
                />
              ) : (
                <ReadValue value={d.tempScale} mono width={32} ariaLabel="Temperature scale" />
              )}
              <BulkFieldCheckbox fieldName="ventSetting" />
              {canEditLc('ventSetting') && tempControlled ? (
                <SelectInput
                  value={s(fv('ventSetting'))}
                  options={VENT_SETTING_OPTIONS}
                  onChange={(v) => upd?.('ventSetting', v)}
                  ariaLabel="Vent setting"
                />
              ) : (
                <ReadValue value={d.ventSetting} ariaLabel="Vent setting" />
              )}
            </Row>
            {renderWarningBox(reeferWarnings)}

            <Divider />

            {/* Whole ESS / Cargo Value row (label + both fields) highlights bright
                yellow when ESS is 'Y', same bg-yellow-300 token as Food Grade. */}
            <Row className={s(fv('highValueCargo')) === 'Y' ? 'bg-yellow-300 rounded px-1' : ''}>
              <Label width={LABEL_W_MID}>ESS / Cargo Value</Label>
              <BulkFieldCheckbox fieldName="highValueCargo" />
              {canEditLc('highValueCargo') ? (
                <SelectInput
                  value={s(fv('highValueCargo'))}
                  options={YN}
                  width={48}
                  onChange={(v) => upd?.('highValueCargo', v)}
                  ariaLabel="ESS"
                />
              ) : (
                <ReadValue value={d.highValueCargo} width={32} ariaLabel="ESS" />
              )}
              <BulkFieldCheckbox fieldName="cargoValue" />
              {canEditLc('cargoValue') ? (
                <TextInput
                  value={s(fv('cargoValue'))}
                  mono
                  onChange={(v) => upd?.('cargoValue', v)}
                  ariaLabel="Cargo value"
                />
              ) : (
                <ReadValue
                  value={d.cargoValue != null ? d.cargoValue.toFixed(2) : undefined}
                  mono
                  ariaLabel="Cargo value"
                />
              )}
            </Row>

            <Divider />

            <Row className="!items-start">
              <Label width={LABEL_W_MID} className="mt-1">
                Hazmat Info
              </Label>
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                {/* Approval checkbox — V1 getHazmatInfo only renders it on a real
                  order (not a template) when a non-exempt line needs approval (or
                  one is already approved). Wrapped in the hazmat-approval
                  PermissionsContainer, which anchors the config cog (permissions
                  mode) + self-registers the component so an admin can grant edit
                  access; defaultDisabled keeps the checkbox read-only until then.
                  The checkbox is also disabled for a do-not-haul line. Stamps via
                  updateOrderField, saved on the footer Save (like template
                  approval). */}
                {!isTemplate && (needHazApproval || hazApprovedEffective) && (
                  <PermissionsContainer
                    componentId={HAZMAT_APPROVAL_COMPONENT_ID}
                    componentName="Hazmat Approval"
                    defaultDisabled
                  >
                    <div
                      className={
                        'flex items-center justify-between gap-2 px-2 py-1 rounded text-[0.71875rem] ' +
                        (hazApprovedEffective
                          ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700'
                          : 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800')
                      }
                      aria-label="Hazmat approval"
                    >
                      <label className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="cursor-pointer disabled:cursor-not-allowed"
                          checked={hazApprovedEffective}
                          disabled={!upd || !canApproveHazmat || hazDonotHaul}
                          onChange={toggleHazApproval}
                          aria-label="Approved for hauling"
                        />
                        <span className="font-semibold uppercase tracking-wide">Approved?</span>
                        {hazApprovedEffective && effHazApprovedBy && (
                          <span className="text-slate-700 dark:text-gray-300">
                            by{' '}
                            <span className="text-slate-900 dark:text-gray-100">
                              {effHazApprovedBy}
                            </span>
                          </span>
                        )}
                      </label>
                      {hazApprovalPending ? (
                        <span className="italic text-[0.625rem] text-slate-500 dark:text-gray-400">
                          unsaved — saves on Save
                        </span>
                      ) : (
                        hazApprovedEffective && hazmatApproval?.approvedDatetime && (
                          <span className="text-slate-600 dark:text-gray-300">
                            {hazmatApproval.approvedDatetime}
                          </span>
                        )
                      )}
                    </div>
                  </PermissionsContainer>
                )}
                {hazDonotHaul && (
                  <div className="text-[0.65625rem] text-red-700 dark:text-red-300">
                    Contains material we do not haul — approval disabled.
                  </div>
                )}
                {hazError && (
                  <div className="text-[0.65625rem] text-red-600 dark:text-red-400">{hazError}</div>
                )}

                {/* Sub-table of hazmat lines */}
                <div className="border border-slate-200 dark:border-gray-700 rounded bg-slate-50 dark:bg-gray-800 max-h-[160px] overflow-y-auto">
                  {hazmatInfo.length === 0 ? (
                    <div className="px-1.5 py-1 text-[0.75rem] text-slate-400 dark:text-gray-500 italic">
                      {PLACEHOLDER}
                    </div>
                  ) : (
                    <table className="w-full text-[0.71875rem]" aria-label="Hazmat lines">
                      <thead className="bg-slate-100 dark:bg-gray-700">
                        <tr>
                          <th className="text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 px-2 py-1 border-b border-slate-200 dark:border-gray-700">
                            UN
                          </th>
                          <th className="text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 px-2 py-1 border-b border-slate-200 dark:border-gray-700">
                            Shipping Name
                          </th>
                          <th className="text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 px-2 py-1 border-b border-slate-200 dark:border-gray-700">
                            Class
                          </th>
                          <th className="text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 px-2 py-1 border-b border-slate-200 dark:border-gray-700">
                            Pkg
                          </th>
                          <th className="text-center text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 px-2 py-1 border-b border-slate-200 dark:border-gray-700">
                            Lim
                          </th>
                          {canEditHazLines && (
                            <th className="w-6 px-1 py-1 border-b border-slate-200 dark:border-gray-700" aria-label="Delete" />
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {hazmatInfo.map((row) => (
                          <tr
                            key={row.recno}
                            className={
                              row.donotHaul
                                ? 'bg-red-50 dark:bg-red-900/20'
                                : row.needApproval
                                  ? 'bg-amber-50 dark:bg-amber-900/20'
                                  : ''
                            }
                          >
                            <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700 whitespace-nowrap">
                              UN{row.hazmatUn || PLACEHOLDER}
                            </td>
                            <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                              {row.shippingName || PLACEHOLDER}
                            </td>
                            <td
                              className={
                                'px-2 py-1 border-b border-slate-100 dark:border-gray-700 ' +
                                (row.donotHaul
                                  ? 'text-red-700 dark:text-red-300 font-semibold'
                                  : row.needApproval
                                    ? 'text-amber-700 dark:text-amber-300 font-semibold'
                                    : 'text-slate-900 dark:text-gray-100')
                              }
                            >
                              {row.hazmatClass || PLACEHOLDER}
                            </td>
                            <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                              {row.hazmatPkg || PLACEHOLDER}
                            </td>
                            <td className="px-2 py-1 text-center text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                              {row.limitedQty === 'Y' ? 'Y' : 'N'}
                            </td>
                            {canEditHazLines && (
                              <td className="px-1 py-1 border-b border-slate-100 dark:border-gray-700 text-center align-middle">
                                <button
                                  type="button"
                                  disabled={hazBusy}
                                  onClick={() => setHazDeleteConfirm({ recno: row.recno, un: row.hazmatUn })}
                                  title={`Delete Hazmat UN ${row.hazmatUn}`}
                                  aria-label={`Delete Hazmat UN ${row.hazmatUn}`}
                                  className="text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Add a hazmat line — opens an anchored popover with the UN
                  lookup (V1 hazmatlookup + savehazmatinfo). The list + delete
                  above stay inline. */}
                {createMode && hazmatInfo.length > 0 && (
                  <div className="text-[0.65625rem] italic text-slate-500 dark:text-gray-400">
                    From template — copied when the order is created.
                  </div>
                )}
                {canEditHazLines && d.pronumber != null && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        setHazAddButtonRect(e.currentTarget.getBoundingClientRect());
                        setHazAddOpen((o) => !o);
                      }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-600 text-white text-[0.6875rem] hover:bg-blue-700"
                      aria-label="Add hazmat line"
                    >
                      <Plus className="w-3 h-3" /> Add line
                    </button>
                    <HazmatAddPopover
                      isOpen={hazAddOpen}
                      pronumber={d.pronumber}
                      buttonRect={hazAddButtonRect}
                      onClose={() => setHazAddOpen(false)}
                      onAdded={() => editing?.refresh?.()}
                    />
                  </>
                )}
              </div>
            </Row>
          </Section>

          {/* COLUMN 3 — Audit / Reuse / Trouble Tickets */}
          <Section title="Audit & Reuse" icon={<Icon.info className="w-3.5 h-3.5" />}>
            <Row>
              <Label width={LABEL_W_RIGHT}>Created By / Date</Label>
              <ReadValue value={d.createdByName || d.createdBy} ariaLabel="Created by" />
              <ReadValue value={d.createdDatetime} mono width={120} ariaLabel="Created datetime" />
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT}>Created From / Parent</Label>
              <div className="flex-1 min-w-0 inline-flex items-center">
                <span
                  className={`inline-flex items-center h-6 px-1.5 text-[0.75rem] truncate ${d.createdFrom ? 'text-slate-900 dark:text-gray-100' : 'text-slate-400 dark:text-gray-500'}`}
                  aria-label="Created from"
                  title={d.createdFrom ? String(d.createdFrom) : undefined}
                >
                  {d.createdFrom || PLACEHOLDER}
                </span>
                {hasEdi && (
                  <button
                    type="button"
                    onClick={() => setEdiModalOpen(true)}
                    className="inline-flex items-center h-6 px-1 text-[0.6875rem] text-blue-600 dark:text-blue-400 hover:underline shrink-0"
                    aria-label="View EDI Info"
                    title="View EDI Info"
                  >
                    View EDI Info
                  </button>
                )}
              </div>
              <ReadValue value={d.parentId} mono width={88} ariaLabel="Parent ID" />
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT}>Template</Label>
              <ReadValue
                value={d.template}
                mono
                ariaLabel="Template"
                href={d.template ? templateSearchUrl(String(d.template)) : undefined}
              />
              {/* Change Template rewrites a persisted order in place — there's
                  nothing to rewrite yet in create mode, where the template was
                  already picked (Template shows the read-only T###). */}
              {!isTemplate && !createMode && d.pronumber != null && (
                <ChangeTemplateTrigger
                  pronumber={d.pronumber}
                  isTemplate={isTemplate}
                  canEdit={canEditOrderLcFields}
                  billTo={{ recno: d.billTo ?? 0, name: d.billToName ?? '' }}
                  customer={{ recno: d.customer ?? 0, name: d.customerName ?? '' }}
                  moveType={d.moveType}
                  currentTemplate={d.template ?? undefined}
                  onApplied={onTemplateChanged}
                />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT}>Last Change / Date</Label>
              <ReadValue value={d.lastChangedByName || d.lastChangedBy} ariaLabel="Last changed by" />
              <ReadValue value={d.lastChangedDatetime} mono width={120} ariaLabel="Last changed datetime" />
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT} bulkFieldName="masterPro" bulkEditable={canEditLc('masterPro')}>{isTemplate ? 'Master Template No.' : 'Master Pro'}</Label>
              {/* Registration anchor for `order-invoicing-fields` — see InvoicingFieldGroup
                  for why the frame sits on this field and not on the other two it governs.
                  It gates nothing itself; canEditLc already ORed the two components. */}
              <InvoicingFieldGroup isTemplate={isTemplate}>
                {canEditLc('masterPro') ? (
                  <>
                    <TextInput
                      value={s(fv('masterPro'))}
                      onChange={(v) => upd?.('masterPro', v)}
                      mono
                      ariaLabel="Master Pro"
                    />
                    {/* Editable input can't be a link itself, so the "all PROs on this
                        master" search (V1 fv_masterpro) rides beside it on the SAVED value. */}
                    {d.masterPro ? (
                      <a
                        href={masterproSearchPath(s(d.masterPro))}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open all PROs on this Master Pro"
                        title="All PROs on this Master PRO (new tab)"
                        className="inline-flex items-center h-6 px-1 shrink-0 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Icon.external className="w-3.5 h-3.5" aria-hidden="true" />
                      </a>
                    ) : null}
                  </>
                ) : (
                  <ReadValue
                    value={d.masterPro}
                    mono
                    ariaLabel="Master Pro"
                    href={masterproSearchPath(s(d.masterPro)) || undefined}
                  />
                )}
              </InvoicingFieldGroup>
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT} bulkFieldName="moveType" bulkEditable={canEditLc('moveType')}>Move Type</Label>
              {canEditLc('moveType') ? (
                <SelectInput
                  value={s(fv('moveType'))}
                  options={MOVE_TYPE_OPTIONS}
                  onChange={(v) => upd?.('moveType', v)}
                  ariaLabel="Move Type"
                />
              ) : (
                <ReadValue value={d.moveType} ariaLabel="Move Type" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT} bulkFieldName="templateType" bulkEditable={canEditLc('templateType')}>Rate Zone</Label>
              {canEditLc('templateType') ? (
                <div className="flex-1 min-w-0">
                  <TypeAhead
                    options={rateZones}
                    value={fv('templateType') ? String(fv('templateType')) : ''}
                    onChange={(next) => upd?.('templateType', next ?? '')}
                    placeholder="Search rate zone…"
                    showSearch
                    compact
                    ariaLabel="Rate Zone"
                  />
                </div>
              ) : (
                <ReadValue value={d.templateType} ariaLabel="Rate Zone" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT}>Region</Label>
              <ReadValue value={d.revenueRegion} ariaLabel="Region" />
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT} bulkFieldName="gwiMove" bulkEditable={canEditLc('gwiMove')}>CMS Code</Label>
              {canEditLc('gwiMove') ? (
                <div className="flex-1 min-w-0">
                  <TypeAhead
                    options={cmsCodes}
                    value={s(fv('gwiMove'))}
                    onChange={(next) => upd?.('gwiMove', next ?? '')}
                    placeholder="Select CMS code…"
                    showSearch
                    compact
                    ariaLabel="CMS Code"
                  />
                </div>
              ) : (
                <ReadValue value={d.gwiMove} mono ariaLabel="CMS Code" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT} bulkFieldName="sendEmailNotifications" bulkEditable={canEditLc('sendEmailNotifications')}>Send Email Notif</Label>
              {canEditLc('sendEmailNotifications') ? (
                <SelectInput
                  value={s(fv('sendEmailNotifications'))}
                  options={YN}
                  width={48}
                  onChange={(v) => upd?.('sendEmailNotifications', v)}
                  ariaLabel="Send email notifications"
                />
              ) : (
                <ReadValue value={d.sendEmailNotifications} width={32} ariaLabel="Send email notifications" />
              )}
            </Row>
            <Row>
              {/* Change-all: the "apply to all" checkbox makes this a BULK delete —
                  the flagged deleted='Y' + the modal reason fan out to the set (each
                  pro re-checked for delete-eligibility). Deliberately NOT in
                  CHANGE_ALL_ELIGIBLE_FIELDS, so the banner's "Flag all edited" sweep
                  never auto-arms a destructive delete — it must be checked explicitly. */}
              <Label width={LABEL_W_RIGHT} bulkFieldName="addlInfoDeleted" bulkEditable={canEditLc('addlInfoDeleted')}>Deleted</Label>
              {/* Inert in create mode: deleting is a server write against a
                  persisted record, and a brand-new order is never deleted. */}
              {canEditLc('addlInfoDeleted') && !createMode ? (
                <span className="inline-flex items-center gap-1.5">
                  <SelectInput
                    value={s(fv('addlInfoDeleted'))}
                    options={YN_ONLY}
                    width={48}
                    disabled={deleteChecking}
                    onChange={(v) => {
                      // Flipping to Y is a delete: hand off to the container for the
                      // eligibility check + reason modal + immediate save (it stages the
                      // 'Y' itself). Un-delete (or no handler) is a plain field edit.
                      if (v === 'Y' && s(fv('addlInfoDeleted')) !== 'Y' && onDeleteAttempt) {
                        onDeleteAttempt();
                      } else {
                        upd?.('addlInfoDeleted', v);
                      }
                    }}
                    ariaLabel="Deleted"
                  />
                  {deleteChecking && (
                    <span
                      className="inline-flex items-center gap-1 text-[0.6875rem] text-slate-500 dark:text-gray-400"
                      aria-live="polite"
                    >
                      <span className="h-3 w-3 animate-spin rounded-full border-b-2 border-slate-400 dark:border-gray-500" />
                      Checking…
                    </span>
                  )}
                </span>
              ) : (
                <ReadValue value={d.addlInfoDeleted} width={32} ariaLabel="Deleted" />
              )}
            </Row>

            <Divider />

            <Row>
              <Label width={LABEL_W_RIGHT}>Reuse Date / Pro</Label>
              <ReadValue value={d.reuseDate} mono width={88} ariaLabel="Reuse date" />
              {/* Clickable pro numbers open the linked pro in a new tab —
                lets dispatch jump between linked moves without losing
                their place on the current order. */}
              {d.reuseContainerPro ? (
                <a
                  href={proDetailPath(d.reuseContainerPro)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[0.75rem] text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                  aria-label="Reuse container Pro"
                >
                  {d.reuseContainerPro}
                </a>
              ) : (
                <ReadValue value={undefined} mono ariaLabel="Reuse container Pro" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT}>Reuse Import Pro</Label>
              {d.reuseImportPro ? (
                <a
                  href={proDetailPath(d.reuseImportPro)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[0.75rem] text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                  aria-label="Reuse import Pro"
                >
                  {d.reuseImportPro}
                </a>
              ) : (
                <ReadValue value={undefined} mono ariaLabel="Reuse import Pro" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT} bulkFieldName="reuseOrigCode" bulkEditable={canEditLc('reuseOrigCode')}>Reuse Origin</Label>
              {canEditLc('reuseOrigCode') ? (
                <div className="flex-1 min-w-0">
                  <LocationTypeAhead
                    value={
                      fv('reuseOrigCode') != null && fv('reuseOrigCode') !== ''
                        ? String(fv('reuseOrigCode'))
                        : ''
                    }
                    valueLabel={String(fv('reuseOrigLocation') ?? '')}
                    onPick={(loc) => {
                      if (!upd) return;
                      upd('reuseOrigCode', loc ? loc.recno : 0);
                      // Optimistically refresh the display name so the user
                      // sees their picked label until the next refetch
                      // overwrites it with the canonical server-joined value.
                      upd('reuseOrigLocation', loc ? locationDisplayName(loc) : '');
                    }}
                    compact
                    withRecnoSuffix
                    className="w-full"
                  />
                </div>
              ) : (
                <ReadValue value={d.reuseOrigLocation} ariaLabel="Reuse origin" />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT} bulkFieldName="reuseStatus" bulkEditable={canEdit('reuseStatus')}>Reuse Status</Label>
              {canEdit('reuseStatus') ? (
                <SelectInput
                  value={String(fv('reuseStatus') ?? '')}
                  options={REUSE_STATUS_VALUES.map((v) => ({
                    value: v,
                    label: v === '' ? '—' : toTitleCase(v),
                  }))}
                  onChange={handleReuseStatusChange}
                  ariaLabel="Reuse status"
                />
              ) : (
                <ReadValue
                  value={d.reuseStatus ? toTitleCase(d.reuseStatus) : ''}
                  ariaLabel="Reuse status"
                />
              )}
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT}>Status By / Date</Label>
              <ReadValue
                value={
                  fv('reuseStatusUserName') ?? fv('reuseStatusUser')
                }
                ariaLabel="Reuse status user"
              />
              <ReadValue
                value={fv('reuseStatusDatetime')}
                mono
                width={120}
                ariaLabel="Reuse status datetime"
              />
            </Row>
            <Row>
              <Label width={LABEL_W_RIGHT}>Requested By / Date</Label>
              <ReadValue value={d.reuseRequestedUserName || d.reuseRequestedUser} ariaLabel="Reuse requested user" />
              <ReadValue value={d.reuseRequestedDatetime} mono width={120} ariaLabel="Reuse requested datetime" />
            </Row>

          </Section>
        </div>
      </div>
      {/* EDI Info — V1 "View EDI" parity. Opened from the Created From / Parent row;
        portals over the drawer. Renders nothing until opened. */}
      <EdiInfoModal open={ediModalOpen} pronumber={d.pronumber} onClose={() => setEdiModalOpen(false)} />
      {/* Entity info popups (V1 parity) — render nothing until their info loads. */}
      <LocationInfoModal location={consigneeInfo} onClose={() => setConsigneeInfo(null)} onUpdated={setConsigneeInfo} />
      <CustomerInfoModal info={customerInfo} onClose={() => setCustomerInfo(null)} />
      <BrokerInfoModal info={brokerInfo} onClose={() => setBrokerInfo(null)} />
      <WarningConfirmModal
        open={hazDeleteConfirm !== null}
        title="Delete Hazmat Line"
        message={`Delete Hazmat UN ${hazDeleteConfirm?.un ?? ''}?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (hazDeleteConfirm) void doDeleteHazLine(hazDeleteConfirm.recno);
          setHazDeleteConfirm(null);
        }}
        onCancel={() => setHazDeleteConfirm(null)}
      />
    </>
  );
};

export default AddlInfoView;
